import * as React from 'react';
import styled from 'styled-components';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import { DataCy } from '../../types/dataAttributes';

const HorizontalWrapper = styled.nav<{ $align: 'center' | 'left' }>`
    border-bottom: 2px solid ${(props) => props.theme.color.grey2};
    display: flex;
    flex-wrap: nowrap;
    margin-bottom: 16px;
    ${(props) => props.$align === 'center' && 'justify-content: space-around;'};
`;

export interface ItemProps {
    isActive: boolean;
}

const HorizontalItem = styled.button<ItemProps>`
    position: relative;
    flex-shrink: 0;
    display: inline-block;
    padding: 4px 16px;
    color: ${(props) => (props.isActive ? (props) => props.theme.color.overseas : (props) => props.theme.color.grey4)};

    &:after {
        content: '';
        transform-origin: center;
        transform: ${(props) => (props.isActive ? 'scaleX(1)' : 'scaleX(0)')};
        transition: 0.5s;
        position: absolute;
        bottom: -2px;
        height: 2px;
        left: 0;
        right: 0;
        background-color: ${(props) => props.theme.color.overseas};
    }
    &:hover {
        color: ${(props) => props.theme.color.grey5};
    }
    &:hover::after {
        transform: scaleX(1);
        transition: 0.5s;
    }
    &:active {
        color: ${(props) => props.theme.color.overseas};
    }
    &:focus {
        background-color: ${(props) => props.theme.color.grey2};
    }
`;

export const VerticalWrapper = styled.ul`
    display: flex;
    flex-shrink: 0;
    margin-right: 32px;
    flex-direction: column;
    width: 230px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.color.grey2};
`;

export const VerticalItem = styled.li``;

export const ItemButton = styled.button<ItemProps>`
    cursor: pointer;
    width: 100%;
    color: ${(props) => (props.isActive ? props.theme.color.overseas : props.theme.color.grey4)};
    background-color: ${(props) => (props.isActive ? props.theme.color.grey1 : props.theme.color.white)};
    &:hover {
        transition: 0.3s;
        background-color: ${(props) => (props.isActive ? props.theme.color.grey1 : props.theme.color.grey0)};
        color: ${(props) => (props.isActive ? props.theme.color.overseas : props.theme.color.grey4)};
    }
    &:focus-within {
        transition: 0.3s;
        background-color: ${(props) => (props.isActive ? props.theme.color.grey1 : props.theme.color.grey0)};
        color: ${(props) => (props.isActive ? props.theme.color.overseas : props.theme.color.grey4)};
    }
`;

export const VerticalItemFlex = styled.div`
    display: flex;
    flex-direction: row;
`;

export const JRCDefaultIconVerticalItem = styled(JRCIcon)`
    margin-left: 7px;
    margin-right: 9px;
`;

/**
 * Interface of a single Menu Item  props
 * @member label label of the tag
 */

export interface ItemEntriesProps<T extends string = string> {
    key: T;
    label: React.ReactNode;
}

/**
 * Interface to provide JRCTabs props
 * @member entries List of Item props @see ItemEntriesProps
 * @member activeItem optional index [0,items.length-1] of the defaultActive item when mounting
 * @member direction direction of tab to horizontal or vertical
 * @member callback tab selection handler
 */
export type JRCTabsProps<T extends string = string> = DataCy & {
    entries?: ItemEntriesProps<T>[];
    activeItem?: T;
    direction: 'horizontal' | 'vertical';
    align?: 'center' | 'left';
    callback: (key: T) => void;
};

/**
 *
 * @param {entries, activeItem, direction} props, @see JRCTabsProps interface
 * @returns JSX.Element
 */
export const JRCTabs = <T extends string = string>({
    entries = [],
    activeItem,
    direction = 'vertical',
    align = 'center',
    callback,
    dataCy,
}: JRCTabsProps<T>): JSX.Element => {
    const clickOnItem = (index: number): void => {
        callback(entries[index].key);
    };

    return (
        <>
            {direction === 'horizontal' ? (
                <HorizontalWrapper $align={align}>
                    {entries.map((tabItem, index) => (
                        <HorizontalItem
                            onClick={() => clickOnItem(index)}
                            key={index}
                            data-cy={dataCy && `${dataCy}-tab-${tabItem.key}`}
                            isActive={activeItem == tabItem.key}>
                            {tabItem.label}
                        </HorizontalItem>
                    ))}
                </HorizontalWrapper>
            ) : (
                <VerticalWrapper>
                    {entries.map((tabItem, index) => (
                        <VerticalItem key={index}>
                            <ItemButton
                                onClick={() => clickOnItem(index)}
                                data-cy={dataCy && `${dataCy}-tab-${tabItem.key}`}
                                isActive={activeItem == tabItem.key}>
                                {tabItem.label}
                            </ItemButton>
                        </VerticalItem>
                    ))}
                </VerticalWrapper>
            )}
        </>
    );
};

/**
 * Interface to provide JRCTabPanel props
 * @member active whether the tab is active
 * @member children children
 */
export interface JRCTabPanelProps {
    active: boolean;
    children: React.ReactNode;
}

export const JRCTabPanel = ({ children, active }: JRCTabPanelProps) => {
    return <>{active && children}</>;
};
